import classNames from 'clsx';
import React from 'react';
import type { ZXCVBNResult, ZXCVBNScore } from 'zxcvbn';

import styles from './PasswordStrength.scss';

export enum Strength {
  Weak = 'weak',
  Meduim = 'medium',
  Strong = 'strong'
}

export interface Props {
  password?: string;
  className?: string;
}

interface State {
  zxcvbn?: (password: string, userInputs?: string[]) => ZXCVBNResult;
}

const scoreLevel = (score: ZXCVBNScore): Strength => {
  // https://github.com/dropbox/zxcvbn#usage
  // 0 # too guessable: risky password. (guesses < 10^3)
  // 1 # very guessable: protection from throttled online attacks. (guesses < 10^6)
  // 2 # somewhat guessable: protection from unthrottled online attacks. (guesses < 10^8)
  // 3 # safely unguessable: moderate protection from offline slow-hash scenario. (guesses < 10^10)
  // 4 # very unguessable: strong protection from offline slow-hash scenario. (guesses >= 10^10)
  if (score >= 3) {
    return Strength.Strong;
  }
  if (score >= 2) {
    return Strength.Meduim;
  }
  return Strength.Weak;
};

export default class PasswordStrength extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {};

    import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn').then(({ default: zxcvbn }) =>
      this.setState({ zxcvbn })
    );
  }

  render() {
    let strength;
    if (this.state.zxcvbn) {
      const { score } = this.state.zxcvbn(this.props.password || '');
      strength = scoreLevel(score);
    }

    return (
      <div
        className={classNames(
          styles.strength,
          {
            [styles.initialized]: !!this.state.zxcvbn,
            [styles[strength as keyof typeof styles]]: this.props.password && strength
          },
          this.props.className
        )}
      />
    );
  }
}
